import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { authAPI, applyAPI } from '../api';
import { toast } from 'sonner';

const BLANK = {
  timezone: '', first_name: '', middle_name: '', last_name: '',
  dob: '', ssn: '', phone: '', bank_account: '', address: '', cdl_number: '',
  experience: '', time_in_city: '', has_cdl: '', why_join: '',
  understands_subscription: '', willing_to_comply: '',
  interview_availability: '',
};

// Required fields per page (page 3 uses signed/consented booleans handled separately)
const PAGE_REQUIRED = [
  ['timezone', 'first_name', 'last_name', 'dob', 'ssn', 'phone', 'bank_account', 'address', 'cdl_number'],
  ['time_in_city', 'has_cdl', 'experience', 'why_join'],
  ['understands_subscription', 'willing_to_comply', 'interview_availability'],
  [],
];

const PAGE_LABEL = ['Personal Information', 'Qualifications', 'Declarations', 'Consent & Signature'];

const PERKS = [
  { icon: '🚛', title: 'Professional Fleet',  tag: 'EQUIPMENT',    desc: 'Access to a maintained fleet of Phantom Custom trucks, ready for any run in the city.' },
  { icon: '💰', title: 'Competitive Pay',      tag: 'COMPENSATION', desc: 'Weekly contractor dues with clear structure — your work, your earnings.' },
  { icon: '🤝', title: 'Tight-Knit Team',      tag: 'CULTURE',      desc: 'A crew that has your back on every run. We operate like a real company.' },
  { icon: '📋', title: 'Clear Structure',      tag: 'CAREER',       desc: 'Defined ranks, SOPs, and growth paths — no guesswork, just progress.' },
];

const TIMEZONES = [
  'UTC-12','UTC-11','UTC-10','UTC-9','UTC-8 (PST)','UTC-7 (MST)','UTC-6 (CST)',
  'UTC-5 (EST)','UTC-4','UTC-3','UTC-2','UTC-1','UTC+0 (GMT)',
  'UTC+1 (CET)','UTC+2','UTC+3','UTC+4','UTC+5','UTC+5:30','UTC+6',
  'UTC+7','UTC+8','UTC+9','UTC+10','UTC+11','UTC+12',
];

/* ── Shared input styles ─────────────────────────────────────────────────── */
const paperInput = [
  'bg-transparent border-0 border-b border-[#b8a99a]',
  'py-1 text-[13px] text-[#1a1209] placeholder-[#c4b5a0]',
  'focus:outline-none focus:border-[#7c3aed] transition-colors',
  'font-mono tracking-wide w-full',
].join(' ');

const paperSelect = [
  'bg-[#faf6ef] border-0 border-b border-[#b8a99a]',
  'py-1 text-[13px] text-[#1a1209]',
  'focus:outline-none focus:border-[#7c3aed] transition-colors',
  'font-mono tracking-wide cursor-pointer w-full',
].join(' ');

const paperTextarea = [
  'bg-transparent border border-[#d4c9bb] rounded',
  'p-2 text-[13px] text-[#1a1209] placeholder-[#c4b5a0]',
  'focus:outline-none focus:border-[#7c3aed] transition-colors resize-none',
  'font-mono tracking-wide leading-relaxed w-full',
].join(' ');

/* ── Sub-components ──────────────────────────────────────────────────────── */
const PaperField = ({ label, required, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[9px] font-black uppercase tracking-[0.18em] text-[#6b5b3e]">
      {label}{required && <span className="text-[#7c3aed] ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const PaperSection = ({ num, title, children }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-3">
      <span className="w-5 h-5 rounded-full bg-[#7c3aed] text-white text-[9px] font-black flex items-center justify-center shrink-0">{num}</span>
      <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4a3728]">{title}</span>
      <div className="flex-1 border-t border-dashed border-[#c4b5a0]" />
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

/* ── 3-D page-flip variants ──────────────────────────────────────────────── */
const flipVariants = {
  enter: (dir) => ({
    rotateY: dir > 0 ? 60 : -60,
    opacity: 0,
    scale: 0.97,
  }),
  center: {
    rotateY: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.52, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: (dir) => ({
    rotateY: dir > 0 ? -60 : 60,
    opacity: 0,
    scale: 0.97,
    transition: { duration: 0.38, ease: [0.55, 0.06, 0.68, 0.19] },
  }),
};

/* ══════════════════════════════════════════════════════════════════════════ */
const JoinUs = () => {
  const [user, setUser]           = useState(null);
  const [authUrl, setAuthUrl]     = useState('');
  const [checkResult, setCheck]   = useState(null);
  const [form, setForm]           = useState(BLANK);
  const [submitting, setSubmit]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [appNumber]               = useState(() => 'GTY-' + String(Math.floor(1000 + Math.random() * 9000)));

  // Pagination
  const [page, setPage]           = useState(0);
  const [direction, setDirection] = useState(1);

  // Signature / consent
  const [signed, setSigned]       = useState(false);
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('gty_token');
    if (!token) {
      authAPI.getDiscordAuthUrl().then(r => setAuthUrl(r.data.url)).catch(() => {}).finally(() => setLoading(false));
      return;
    }
    authAPI.getUserProfile()
      .then(r => { setUser(r.data); return applyAPI.check(); })
      .then(r => setCheck(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  /* Navigate forward — validates current page first */
  const goNext = () => {
    const missing = PAGE_REQUIRED[page].filter(k => !form[k].trim());
    if (missing.length) { toast.error('Please fill in all required fields before continuing.'); return; }
    setDirection(1);
    setPage(p => p + 1);
  };

  const goPrev = () => {
    setDirection(-1);
    setPage(p => p - 1);
  };

  const handleSubmit = async () => {
    if (!signed)    { toast.error('Please sign the application.'); return; }
    if (!consented) { toast.error('Please check the consent box before submitting.'); return; }
    setSubmit(true);
    try {
      await applyAPI.submit(form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmit(false);
    }
  };

  const handleSign = () => {
    if (signed) return;
    if (!form.last_name.trim()) { toast.error('Fill in your last name on page 1 first.'); return; }
    setSigned(true);
  };

  if (loading) return (
    <div className="min-h-screen pt-24 flex items-center justify-center bg-[#0a0a0c]">
      <span className="text-[#71717a] text-xs uppercase tracking-[0.2em] font-black">Loading…</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0c] pt-16">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');

        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.25} }
        @keyframes stamp-in {
          0%   { transform: scale(2.8) rotate(-12deg); opacity: 0; }
          55%  { transform: scale(0.9) rotate(-6deg); opacity: 1; }
          75%  { transform: scale(1.05) rotate(-6deg); }
          100% { transform: scale(1) rotate(-6deg); opacity: 1; }
        }
        @keyframes note-float-a { 0%,100%{transform:rotate(-2.5deg) translateY(0px)} 50%{transform:rotate(-2.5deg) translateY(-5px)} }
        @keyframes note-float-b { 0%,100%{transform:rotate(1.8deg) translateY(0px)} 50%{transform:rotate(1.8deg) translateY(-4px)} }
        @keyframes note-float-c { 0%,100%{transform:rotate(-1.2deg) translateY(0px)} 50%{transform:rotate(-1.2deg) translateY(-6px)} }
        @keyframes note-float-d { 0%,100%{transform:rotate(2.5deg) translateY(0px)} 50%{transform:rotate(2.5deg) translateY(-4px)} }
        .note-a { animation: note-float-a 4.2s ease-in-out infinite; }
        .note-b { animation: note-float-b 4.8s ease-in-out infinite; }
        .note-c { animation: note-float-c 3.9s ease-in-out infinite; }
        .note-d { animation: note-float-d 4.5s ease-in-out infinite; }
        .blink  { animation: blink 1.3s step-end infinite; }
        .stamp  { animation: stamp-in 0.5s cubic-bezier(0.36,0.07,0.19,0.97) both; }

        .stripe-bg {
          background-image: repeating-linear-gradient(
            -45deg,
            rgba(139,92,246,0.18) 0px, rgba(139,92,246,0.18) 10px,
            transparent 10px, transparent 20px
          );
        }
        .paper-texture {
          background-color: #faf6ef;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
        }

        /* Signature write-in */
        @keyframes write-in {
          0%   { opacity: 0; clip-path: inset(0 100% 0 0); }
          100% { opacity: 1; clip-path: inset(0 0% 0 0); }
        }
        .signature-text {
          font-family: 'Dancing Script', cursive;
          font-weight: 700;
          font-size: 1.75rem;
          line-height: 1;
          color: #1a3a6e;
          animation: write-in 0.55s ease-out forwards;
          display: inline-block;
          pointer-events: none;
          user-select: none;
        }
        .sig-field-empty {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='%231a3a6e' d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'/%3E%3C/svg%3E") 4 20, crosshair;
        }

        /* Page-corner fold */
        .page-corner-wrap {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 52px;
          height: 52px;
          overflow: hidden;
          cursor: pointer;
        }
        .page-corner-fold {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0 0 52px 52px;
          border-color: transparent transparent #c4b5a4 transparent;
          transition: border-width 0.18s ease;
          filter: drop-shadow(-2px -2px 4px rgba(0,0,0,0.18));
        }
        .page-corner-wrap:hover .page-corner-fold {
          border-width: 0 0 68px 68px;
        }
        .page-corner-label {
          position: absolute;
          bottom: 7px;
          right: 7px;
          font-size: 9px;
          font-weight: 900;
          color: #6b5b3e;
          pointer-events: none;
          transition: opacity 0.18s;
          line-height: 1;
        }
      `}</style>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden border-b-2 border-[#6d28d9]/30">
        <div className="stripe-bg h-2 w-full" />
        <div className="relative px-6 md:px-12 py-14 md:py-20 max-w-5xl mx-auto">
          <div className="absolute top-8 right-6 md:right-12 text-[10px] font-black text-[#3f3f46] uppercase tracking-widest">
            Form #GTY-APP-001
          </div>
          <div className="flex flex-col md:flex-row md:items-end gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-5">
                <span className="blink w-2 h-2 rounded-full bg-[#a78bfa] shrink-0" />
                <span className="text-[10px] font-black uppercase tracking-[0.25em] text-[#a78bfa]">Positions Open</span>
              </div>
              <h1 className="text-[clamp(3.5rem,10vw,8rem)] font-black leading-[0.88] tracking-tighter uppercase text-white mb-4">
                NOW<br/><span className="text-[#a78bfa]">HIRING</span>
              </h1>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px flex-1 bg-gradient-to-r from-[#7c3aed]/60 to-transparent" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#71717a] shrink-0">Go Truck Yourself — Trucking Division</span>
              </div>
              <p className="text-[#a1a1aa] text-sm leading-relaxed max-w-lg">
                We're looking for dedicated contractors to join GTY's professional fleet.
                If you take pride in your work, follow the road, and want to be part of something real — this is your stop.
              </p>
            </div>
            <div className="shrink-0 border border-[#1c1c1e] p-5 space-y-4 min-w-[160px]">
              <div>
                <div className="text-3xl font-black text-white">22</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#71717a]">Fleet Trucks</div>
              </div>
              <div className="h-px bg-[#1c1c1e]" />
              <div>
                <div className="text-3xl font-black text-[#a78bfa]">24/7</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#71717a]">Operations</div>
              </div>
              <div className="h-px bg-[#1c1c1e]" />
              <div>
                <div className="text-3xl font-black text-white">GTY</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#71717a]">Est. BadlandsRP</div>
              </div>
            </div>
          </div>
        </div>
        <div className="stripe-bg h-2 w-full" />
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">

        {/* ── PERKS — sticky notes ─────────────────────────────────────── */}
        {!showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <div className="mb-4">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#52525b]">Why join us</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
              {[
                { note: 'note-a', bg: '#ffe066', shadow: '#c9a800' },
                { note: 'note-b', bg: '#b6f5c8', shadow: '#3a9e5a' },
                { note: 'note-c', bg: '#c7e8ff', shadow: '#3a7fbf' },
                { note: 'note-d', bg: '#ffc8e8', shadow: '#b03070' },
              ].map(({ note, bg, shadow }, i) => (
                <motion.div key={PERKS[i].title} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.09 }} className={`${note} relative`} style={{ transformOrigin: 'center top' }}>
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10 w-4 h-4 rounded-full shadow-md"
                    style={{ background: '#555', border: '2px solid #333' }} />
                  <div className="rounded-sm p-5 pb-6" style={{ background: bg, boxShadow: `3px 5px 0 ${shadow}40, 0 8px 24px rgba(0,0,0,0.25)` }}>
                    <div className="text-[9px] font-black uppercase tracking-[0.18em] mb-2" style={{ color: shadow }}>{PERKS[i].tag}</div>
                    <div className="text-2xl mb-2">{PERKS[i].icon}</div>
                    <p className="font-black text-sm uppercase tracking-tight leading-tight mb-2 text-[#1a1a1a]">{PERKS[i].title}</p>
                    <p className="text-xs leading-relaxed text-[#2d2d2d]">{PERKS[i].desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-6 border border-[#1c1c1e] p-6">
              <div className="flex-1">
                <p className="text-white font-black text-sm uppercase tracking-wider mb-1">Ready to apply?</p>
                <p className="text-[#71717a] text-xs">The application takes about 5 minutes. Have your IC details ready.</p>
              </div>
              <button onClick={() => setShowForm(true)}
                className="shrink-0 px-8 py-3.5 bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-black uppercase tracking-[0.15em] text-xs transition-colors duration-150">
                Apply Now →
              </button>
            </motion.div>
          </motion.div>
        )}

        {/* ── PAPER APPLICATION ────────────────────────────────────────── */}
        {showForm && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>

            {/* Not logged in */}
            {!user && (
              <div className="border border-[#1c1c1e] p-10 text-center">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#71717a] mb-6">Discord authentication required</p>
                <a href={authUrl} className="inline-block bg-[#5865F2] hover:bg-[#4752C4] text-white font-black px-10 py-3 uppercase tracking-widest text-xs transition-colors">
                  Connect Discord
                </a>
              </div>
            )}

            {user && checkResult?.reason === 'blocked' && (
              <div className="border border-red-900/40 p-8 text-center">
                <p className="text-red-400 font-black text-xs uppercase tracking-[0.2em]">You are not eligible to apply.</p>
              </div>
            )}

            {user && checkResult?.reason === 'existing' && !submitted && (
              <div className="border border-[#1c1c1e] p-8 text-center space-y-3">
                <p className="text-[#a78bfa] font-black text-xs uppercase tracking-[0.2em]">Application Already On File</p>
                <p className="text-[#71717a] text-sm">Current status: <span className="text-white font-black capitalize">{checkResult.status?.replace('_', ' ')}</span></p>
                <p className="text-[#52525b] text-xs">You will be notified via Discord DM when your status changes.</p>
              </div>
            )}

            {/* ── THE PAPER DOCUMENT ────────────────────────────────── */}
            {((user && checkResult?.can_apply && !submitted) || submitted) && (
              <div className="relative">
                {/* Stack shadow layers — static, outside the flip */}
                <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#e0d8cc] rounded-sm" />
                <div className="absolute inset-0 translate-x-1 translate-y-1 bg-[#ede8df] rounded-sm" />

                {/* Perspective wrapper — the entire paper flips */}
                <div style={{ perspective: '1400px' }}>
                  <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                      key={submitted ? 'submitted' : page}
                      custom={direction}
                      variants={flipVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      style={{ transformOrigin: direction > 0 ? '15% center' : '85% center' }}
                      className="relative paper-texture rounded-sm shadow-[0_8px_40px_rgba(0,0,0,0.5)] overflow-hidden"
                    >

                  {/* Hole punches left margin */}
                  <div className="absolute left-0 top-0 bottom-0 w-14 border-r border-dashed border-[#d4c9bb] z-10 pointer-events-none">
                    {[20, 45, 70].map(pct => (
                      <div key={pct} className="absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#e8e0d8]"
                        style={{ top: `${pct}%`, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.15)' }} />
                    ))}
                  </div>

                  <div className="pl-20 pr-8 pt-8 pb-10">

                    {/* ── Document header ────────────────────────────── */}
                    <div className="flex items-start justify-between mb-4 pb-4 border-b-2 border-[#1a1209]">
                      <div className="flex items-center gap-4">
                        <img src="/logo.png" alt="GTY" className="w-14 h-14 object-contain opacity-90" />
                        <div>
                          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-[#1a1209]">Go Truck Yourself</div>
                          <div className="text-[9px] font-bold text-[#6b5b3e] tracking-wide">Professional Trucking — BadlandsRP</div>
                          <div className="text-[9px] text-[#9b8b74] mt-0.5">Contractor Division · Employment Records</div>
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1.5">
                        <div>
                          <div className="text-[9px] font-black uppercase tracking-[0.15em] text-[#6b5b3e]">Application No.</div>
                          <div className="text-lg font-black text-[#1a1209] font-mono tracking-wider">{appNumber}</div>
                          <div className="text-[9px] text-[#9b8b74] mt-0.5">{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                        </div>
                        {/* Page dots */}
                        {!submitted && (
                          <div className="flex items-center gap-1.5">
                            {[0,1,2,3].map(i => (
                              <div key={i} className={`rounded-full transition-all duration-200 ${i === page ? 'w-3 h-1.5 bg-[#7c3aed]' : 'w-1.5 h-1.5 bg-[#d4c9bb]'}`} />
                            ))}
                            <span className="text-[9px] text-[#9b8b74] ml-1 font-mono">{page + 1}/4</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Document title ─────────────────────────────── */}
                    <div className="text-center mb-6">
                      <h2 className="text-sm font-black uppercase tracking-[0.3em] text-[#1a1209]">Contractor Employment Application</h2>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex-1 h-px bg-[#b8a99a]" />
                        <span className="text-[9px] font-bold text-[#9b8b74] tracking-wider uppercase">
                          {submitted ? 'Filed · Confidential' : `Page ${page + 1} of 4 — ${PAGE_LABEL[page]}`}
                        </span>
                        <div className="flex-1 h-px bg-[#b8a99a]" />
                      </div>
                    </div>

                    {/* ── SUBMITTED STATE ───────────────────────────── */}
                    {submitted && (
                      <div className="relative py-8 text-center">
                        <div className="stamp inline-block border-4 border-[#16a34a] text-[#16a34a] px-8 py-4 font-black text-xl uppercase tracking-[0.2em]"
                          style={{ fontFamily: 'monospace', transform: 'rotate(-6deg)', display: 'inline-block' }}>
                          RECEIVED ✓
                        </div>
                        <p className="mt-6 text-[#4a3728] text-sm">Your application has been filed under <strong className="font-black">{appNumber}</strong>.</p>
                        <p className="text-[#6b5b3e] text-xs mt-1">You will receive a Discord DM confirmation shortly.</p>
                      </div>
                    )}

                    {/* ── FORM PAGES (no inner AnimatePresence — entire paper flips) ── */}
                    {!submitted && (
                      <div>

                            {/* ══ PAGE 0 — Personal Information ══ */}
                            {page === 0 && (
                              <div className="space-y-6">
                                <div className="bg-[#7c3aed]/8 border border-[#7c3aed]/20 rounded px-4 py-2.5 flex items-start gap-3">
                                  <span className="text-[#7c3aed] font-black text-xs shrink-0 mt-0.5">NOTE</span>
                                  <p className="text-[11px] text-[#4a3728] leading-relaxed">
                                    This application is <strong>private</strong> — only you and GTY Management can view it.
                                    Answer all fields honestly and with as much detail as possible.
                                  </p>
                                </div>

                                <PaperSection num="01" title="Out of Character">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <PaperField label="Discord Username" required>
                                      <input className={paperInput + ' opacity-50 cursor-not-allowed'} value={user.username || user.display_name || ''} readOnly />
                                    </PaperField>
                                    <PaperField label="Time Zone" required>
                                      <select className={paperSelect} value={form.timezone} onChange={f('timezone')}>
                                        <option value="">— Select —</option>
                                        {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                                      </select>
                                    </PaperField>
                                  </div>
                                </PaperSection>

                                <PaperSection num="02" title="In Character — Personal Information">
                                  <div className="grid grid-cols-3 gap-4">
                                    <PaperField label="First Name" required>
                                      <input className={paperInput} value={form.first_name} onChange={f('first_name')} placeholder="John" />
                                    </PaperField>
                                    <PaperField label="Middle Name">
                                      <input className={paperInput} value={form.middle_name} onChange={f('middle_name')} placeholder="(optional)" />
                                    </PaperField>
                                    <PaperField label="Last Name" required>
                                      <input className={paperInput} value={form.last_name} onChange={f('last_name')} placeholder="Doe" />
                                    </PaperField>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <PaperField label="Date of Birth" required>
                                      <input className={paperInput} value={form.dob} onChange={f('dob')} placeholder="MM/DD/YY" />
                                    </PaperField>
                                    <PaperField label="SSN / Player ID (F6#)" required>
                                      <input className={paperInput} value={form.ssn} onChange={f('ssn')} placeholder="123456" />
                                    </PaperField>
                                    <PaperField label="Phone Number" required>
                                      <input className={paperInput} value={form.phone} onChange={f('phone')} placeholder="555-0100" />
                                    </PaperField>
                                    <PaperField label="Main Bank Account" required>
                                      <input className={paperInput} value={form.bank_account} onChange={f('bank_account')} placeholder="$0" />
                                    </PaperField>
                                  </div>
                                  <PaperField label="Residential Address" required>
                                    <input className={paperInput} value={form.address} onChange={f('address')} placeholder="123 Main St, Los Santos" />
                                  </PaperField>
                                  <PaperField label="CDL / Driver License Number" required>
                                    <input className={paperInput} value={form.cdl_number} onChange={f('cdl_number')} placeholder="DL-XXXXXXX" />
                                  </PaperField>
                                </PaperSection>
                              </div>
                            )}

                            {/* ══ PAGE 1 — Qualifications ══ */}
                            {page === 1 && (
                              <div>
                                <PaperSection num="03" title="Screening Questions">
                                  <div className="grid grid-cols-2 gap-4">
                                    <PaperField label="Time in the city" required>
                                      <input className={paperInput} value={form.time_in_city} onChange={f('time_in_city')} placeholder="e.g. 6 months" />
                                    </PaperField>
                                    <PaperField label="Do you hold a CDL?" required>
                                      <select className={paperSelect} value={form.has_cdl} onChange={f('has_cdl')}>
                                        <option value="">— Select —</option>
                                        <option>Yes</option>
                                        <option>No</option>
                                        <option>In progress</option>
                                      </select>
                                    </PaperField>
                                  </div>
                                  <PaperField label="Driving & trucking experience" required>
                                    <textarea className={paperTextarea} rows={4} value={form.experience} onChange={f('experience')}
                                      placeholder="Describe your experience with trucking or driving in the city…" />
                                  </PaperField>
                                  <PaperField label="Why do you want to join Go Truck Yourself?" required>
                                    <textarea className={paperTextarea} rows={5} value={form.why_join} onChange={f('why_join')}
                                      placeholder="Be specific — what draws you to GTY over other companies?" />
                                  </PaperField>
                                </PaperSection>
                              </div>
                            )}

                            {/* ══ PAGE 2 — Declarations ══ */}
                            {page === 2 && (
                              <div className="space-y-6">
                                <PaperSection num="04" title="Declarations & Compliance">
                                  <PaperField label="Do you understand this is a weekly subscription-based contract?" required>
                                    <select className={paperSelect} value={form.understands_subscription} onChange={f('understands_subscription')}>
                                      <option value="">— Select —</option>
                                      <option value="Yes, I understand">Yes, I understand</option>
                                      <option value="No">No</option>
                                    </select>
                                  </PaperField>
                                  <PaperField label="Are you willing to comply with GTY policies and road safety standards at all times?" required>
                                    <select className={paperSelect} value={form.willing_to_comply} onChange={f('willing_to_comply')}>
                                      <option value="">— Select —</option>
                                      <option value="Yes">Yes</option>
                                      <option value="No">No</option>
                                    </select>
                                  </PaperField>
                                </PaperSection>

                                <PaperSection num="05" title="Interview Availability">
                                  <div className="bg-amber-50 border border-amber-300 rounded px-4 py-2.5 flex items-start gap-3">
                                    <span className="text-amber-700 font-black text-xs shrink-0 mt-0.5">NOTE</span>
                                    <p className="text-[11px] text-amber-800 leading-relaxed">
                                      Please provide a specific date and time (e.g. <em>'Monday April 28th after 6pm EST'</em>). Vague responses
                                      such as <em>'right now'</em>, <em>'today'</em>, or <em>'this week'</em> are <strong>NOT</strong> considered adequate and may delay your application.
                                    </p>
                                  </div>
                                  <PaperField label="When are you available for an interview?" required>
                                    <textarea className={paperTextarea} rows={3} value={form.interview_availability} onChange={f('interview_availability')}
                                      placeholder="e.g. Monday April 28th after 6pm EST, or weekdays after 5pm UTC+1" />
                                  </PaperField>
                                </PaperSection>
                              </div>
                            )}

                            {/* ══ PAGE 3 — Consent & Signature ══ */}
                            {page === 3 && (
                              <div className="space-y-6">

                                {/* Declaration box */}
                                <div className="border border-[#b8a99a] rounded p-5 bg-[#f5f0e8] space-y-3">
                                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#4a3728] mb-1">Applicant Declaration & Consent</div>
                                  <p className="text-[11px] text-[#4a3728] leading-relaxed">
                                    By signing this application, I confirm that all information provided is truthful and accurate to the best of my knowledge.
                                    I understand that any false statements may result in immediate disqualification or termination of contract.
                                  </p>
                                  <p className="text-[11px] text-[#4a3728] leading-relaxed">
                                    I acknowledge that communications conducted through the GTY hiring process — including messages sent to or received
                                    from the GTY Discord bot — are <strong>monitored and reviewed by GTY Management</strong> for the purposes of the application
                                    and interview process.
                                  </p>
                                  <p className="text-[11px] text-[#4a3728] leading-relaxed">
                                    I agree to abide by all GTY policies, SOPs, and conduct standards if hired, and understand this is a contractor
                                    arrangement within the BadlandsRP environment.
                                  </p>
                                </div>

                                {/* Consent checkbox */}
                                <label className="flex items-start gap-3 cursor-pointer select-none" onClick={() => setConsented(c => !c)}>
                                  <div className={`mt-0.5 w-4 h-4 shrink-0 border-2 flex items-center justify-center transition-colors ${consented ? 'border-[#7c3aed] bg-[#7c3aed]' : 'border-[#b8a99a] bg-transparent hover:border-[#7c3aed]'}`}>
                                    {consented && <span className="text-white text-[9px] font-black leading-none">✓</span>}
                                  </div>
                                  <span className="text-[11px] text-[#4a3728] leading-relaxed">
                                    I have read, understood, and agree to the above declaration. I consent to GTY monitoring application-related communications for hiring purposes.
                                  </span>
                                </label>

                                {/* Signature row */}
                                <div className="pt-5 border-t-2 border-dashed border-[#b8a99a]">
                                  <div className="grid grid-cols-2 gap-8">
                                    {/* Signature field */}
                                    <div>
                                      <div
                                        onClick={handleSign}
                                        className={`h-12 border-b-2 mb-1 flex items-end pb-1 transition-colors ${signed ? 'border-[#1a3a6e]' : 'border-[#b8a99a] sig-field-empty hover:border-[#7c3aed]'}`}
                                      >
                                        {signed ? (
                                          <span className="signature-text">
                                            {form.first_name ? `${form.first_name} ${form.last_name}` : form.last_name}
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-[#c4b5a0] italic select-none leading-none mb-0.5">
                                            Click to sign…
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-[9px] font-black uppercase tracking-[0.15em] text-[#9b8b74]">Applicant Signature</div>
                                      {!signed && (
                                        <div className="text-[8px] text-[#b8a99a] mt-0.5 italic">Uses your in-character name from page 1</div>
                                      )}
                                    </div>
                                    {/* Auto date */}
                                    <div>
                                      <div className="h-12 border-b-2 border-[#b8a99a] mb-1 flex items-end pb-1">
                                        <span className="text-[13px] text-[#1a1209] font-mono">
                                          {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </span>
                                      </div>
                                      <div className="text-[9px] font-black uppercase tracking-[0.15em] text-[#9b8b74]">Date</div>
                                    </div>
                                  </div>
                                </div>

                              </div>
                            )}

                      </div>
                    )}

                    {/* ── NAVIGATION BAR ────────────────────────────── */}
                    {!submitted && (
                      <div className="flex items-center justify-between pt-6 mt-6 border-t border-dashed border-[#b8a99a]">
                        {/* Prev */}
                        <div>
                          {page > 0 ? (
                            <button type="button" onClick={goPrev}
                              className="text-[11px] font-black uppercase tracking-[0.15em] text-[#9b8b74] hover:text-[#4a3728] transition-colors flex items-center gap-1.5">
                              ← Previous
                            </button>
                          ) : <div />}
                        </div>

                        {/* Progress bar */}
                        <div className="flex items-center gap-1.5">
                          {[0,1,2,3].map(i => (
                            <div key={i} className={`h-0.5 transition-all duration-300 ${i <= page ? 'bg-[#7c3aed] w-8' : 'bg-[#d4c9bb] w-5'}`} />
                          ))}
                        </div>

                        {/* Next / Submit */}
                        <div>
                          {page < 3 ? (
                            <button type="button" onClick={goNext}
                              className="px-6 py-2.5 bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-black uppercase tracking-[0.15em] text-[11px] transition-colors">
                              Next →
                            </button>
                          ) : (
                            <button type="button" onClick={handleSubmit}
                              disabled={submitting || !signed || !consented}
                              className="px-8 py-3 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:opacity-40 disabled:cursor-not-allowed text-white font-black uppercase tracking-[0.15em] text-xs transition-colors duration-150">
                              {submitting ? 'Filing…' : 'Submit Application →'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Footer ref line */}
                    {!submitted && (
                      <div className="flex items-center justify-between pt-3 mt-1">
                        <div>
                          <div className="text-[9px] font-black uppercase tracking-[0.15em] text-[#9b8b74]">Ref: {appNumber}</div>
                          <div className="text-[9px] text-[#b8a99a]">GTY Employment Division · BadlandsRP</div>
                        </div>
                        {page < 3 && (
                          <span className="text-[9px] text-[#c4b5a0] italic">
                            Tip: click the corner fold ↗ to advance
                          </span>
                        )}
                      </div>
                    )}

                    </div>{/* end pl-20 */}

                  {/* ── Page corner fold (forward only) ─────────────── */}
                  {!submitted && page < 3 && (
                    <div className="page-corner-wrap" onClick={goNext} title="Next page">
                      <div className="page-corner-fold" />
                      <span className="page-corner-label">→</span>
                    </div>
                  )}

                    </motion.div>
                  </AnimatePresence>
                </div>{/* end perspective wrapper */}
              </div>
            )}

          </motion.div>
        )}
      </div>
    </div>
  );
};

export default JoinUs;
